import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EntityCellValue,
  EntityKind,
  Fact,
  FactValue,
  ShallowEntity,
  ShallowSearchResult,
  SchemaFact,
} from "../types";
import EntityIcon from "./EntityIcon";
import { entityKindMap } from "../utils";
import {
  searchEntities,
  createEntity,
  fetchEntitySchema,
  fetchNicknames,
} from "../api";
import {
  buildNicknameMap,
  NicknameMap,
  shouldOfferCreateRow,
} from "./entityCreateGate";
import EntityCard from "../Cards/EntityCard";
import { X, Plus } from "lucide-react";
import { useEntityNavigate } from "../entityNavigation";
import EntityCreationModal from "../../Setup/Create/EntityCreationModal";
import { prefetchAttrs, useEntitySchema } from "../../Data";
import { useRecents } from "../../useRecents";
import { useDebouncedValue } from "@/hooks/productTimerHooks";
import {
  DROPDOWN_VIEWPORT_GUTTER_PX,
  dropdownMaxHeight,
} from "./entitySearchDropdown";

interface EntitySearchInputProps {
  value: ShallowEntity | null;
  entityTypes?: EntityKind[];
  onSelect: (entity: ShallowEntity | null) => void;
  placeholder?: string;
  /** The field's accessible name, where no visible label names it. */
  ariaLabel?: string;
  variant?: "search" | "dropdown";
  selectedEntities?: ShallowEntity[]; // Array of entities to exclude from search results
  // Entities currently held by a sibling bucket (e.g. Mentions when this picker is for Participants).
  // Unlike selectedEntities, these are NOT filtered out — they appear in results with `label`
  // appended to the subheader, signaling that selecting them moves the entity from that bucket.
  crossBucketEntities?: { entity: ShallowEntity; label: string }[];
  excludeEntityId?: string | null; // Entity ID to exclude from search results (e.g., current entity to prevent self-reference)
  allow_create_person?: boolean; // Allow fast create for person entities
  allow_create_entity?: boolean; // Allow create for entities (single type = fast create, multiple types = modal)
  // The record kind the create modal should go straight to, skipping its type
  // chooser, when this picker accepts more than one kind but the attribute
  // already determines which one a new record is (#11662). Search results are
  // unaffected -- the attribute still holds, and still finds, every kind it
  // accepts.
  createEntityKind?: EntityKind | null;
  autoFocus?: boolean; // Auto-focus the input when mounted
  tableCell?: boolean; // Compact styling for table cells - portals dropdown and hides magnifying glass
  initialSearchTerm?: string | null; // Initial search term when entering edit mode by typing
  blurAfterSelect?: boolean; // Blur (defocus) the input after selection instead of refocusing. Defaults to false.
  showRecents?: boolean; // Show recently viewed items when focused with empty search term
  hideTypeIndicators?: boolean; // Hide entity type icons and labels in search results
  includeInteractions?: boolean; // Include interactions in search results
  portalDropdown?: boolean; // Portal the dropdown to document.body (needed when rendered inside a clipping container like the filter panel)
  prefetchResults?: boolean; // Emit data-prefetch-* attrs on dropdown rows. Opt-in so callers nested inside other prefetch-observer subtrees don't accidentally trigger prefetches.
  leadingIconOffset?: string; // Tailwind `left-*` class for the magnifying glass / entity icon. Defaults to `left-3`; callers that render a chip to the left of this input (e.g. the Autofill chip in EditableFactRenderer) pass a wider value so the icon clears the chip.
  inputClassName?: string; // Extra classes appended to the input element — the RMS topbar passes the mock's field ground (page bg, strong border) here.
  dense?: boolean; // The topbar's scale (#11426 gaps-v7 item 8): meta-size type on a 30px box, instead of text-sm on ~38px. The hit area stays 30px+ — the type shrinks, the target does not (CD-8).
}

// TODO: We want to make it configurable which attributes are pulled for a preview in the subheader.
// For now, "Employer" on people and "Status" on vehicles are relevant.
export const SPECIAL_PERSON_ATTRIBUTE = "Employer";
export const SPECIAL_VEHICLE_ATTRIBUTE = "Status";

// The in-flow dropdown sits this far below the field.
const IN_FLOW_DROPDOWN_OFFSET_PX = 4;
// The in-flow dropdown's cap while the field is unmeasured: the list can show
// during a search's first load before the dropdown is marked open, and the
// field is measured only once it is.
const UNMEASURED_DROPDOWN_MAX_HEIGHT_PX = 384;

// Where the field sits in the viewport while the dropdown is open. Only the
// portalled dropdown is placed horizontally from it.
type DropdownPosition = {
  top: number;
  inputTop: number;
  viewportHeight: number;
  horizontal: { left: number; width: number } | null;
};

const samePosition = (a: DropdownPosition, b: DropdownPosition): boolean =>
  a.top === b.top &&
  a.inputTop === b.inputTop &&
  a.viewportHeight === b.viewportHeight &&
  a.horizontal?.left === b.horizontal?.left &&
  a.horizontal?.width === b.horizontal?.width;

// The nickname table, fetched once per app session and shared by every
// picker through the query cache (#12137). Empty until it lands, and empty
// for good if the fetch fails: the gate then degrades to plain containment.
const EMPTY_NICKNAMES: NicknameMap = {};
const useNicknames = (): NicknameMap => {
  const { data } = useQuery({
    queryKey: ["rms", "nicknames"],
    queryFn: fetchNicknames,
    select: buildNicknameMap,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  return data ?? EMPTY_NICKNAMES;
};

// Format name for fast person creation: convert lowercase starts to title case,
// but preserve all-caps words (like "CPA", "LLC", etc.)
const formatPersonName = (name: string): string => {
  return name
    .trim()
    .split(/\s+/)
    .map((word) => {
      // If word is all uppercase (and has at least 2 characters), preserve it
      if (
        word.length >= 2 &&
        word === word.toUpperCase() &&
        /^[A-Z]+$/.test(word)
      ) {
        return word;
      }
      // If word starts with lowercase, capitalize first letter only
      if (word.length > 0 && word[0] === word[0].toLowerCase()) {
        return word[0].toUpperCase() + word.slice(1);
      }
      // Otherwise, preserve as-is (already has uppercase start)
      return word;
    })
    .join(" ");
};

const EntitySearchInput: React.FC<EntitySearchInputProps> = ({
  value,
  entityTypes,
  onSelect,
  placeholder,
  ariaLabel,
  variant = "search",
  selectedEntities = [],
  crossBucketEntities = [],
  excludeEntityId = null,
  allow_create_person = false,
  allow_create_entity = false,
  createEntityKind = null,
  autoFocus = false,
  tableCell = false,
  initialSearchTerm = null,
  blurAfterSelect = false,
  showRecents = false,
  hideTypeIndicators = false,
  includeInteractions = false,
  portalDropdown = false,
  prefetchResults = false,
  leadingIconOffset = "left-3",
  inputClassName = "",
  dense = false,
}) => {
  const queryClient = useQueryClient();
  const entityNavigate = useEntityNavigate();
  const nicknameMap = useNicknames();
  const { recents, hasRecents, refreshFromStorage } = useRecents();
  const maybePrefetchAttrs = prefetchResults ? prefetchAttrs : () => ({});
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || "");
  const [rawSearchResults, setRawSearchResults] = useState<
    ShallowSearchResult[]
  >([]);
  const [backendShowCreateButton, setBackendShowCreateButton] = useState(false);
  const [createButtonName, setCreateButtonName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [hasFocused, setHasFocused] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isCreatingPerson, setIsCreatingPerson] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreatingEntity, setIsCreatingEntity] = useState(false);
  const [showEntityCreateModal, setShowEntityCreateModal] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Enter pressed while the typed term's search was still in flight
  // (#11962): honoured against that term's results when they land, never
  // against an older term's.
  const pendingEnterRef = useRef(false);

  // If we have an initial search term, clear the selected value and set search term
  useEffect(() => {
    if (initialSearchTerm && initialSearchTerm.trim()) {
      // Mark that we're processing an initial search term to prevent other effects from clearing it
      isProcessingInitialSearchTermRef.current = true;

      // Clear selected value if present
      if (value) {
        onSelect(null);
      }
      // Set search term to the initial typed character
      setSearchTerm(initialSearchTerm);
      setDebouncedSearchTerm(initialSearchTerm);
      setHasFocused(true);
      setInitialSearchTermGeneration((g) => g + 1);
    }
  }, [initialSearchTerm]); // Only run when initialSearchTerm changes

  // Bumped every time the effect above processes an initial search term;
  // the effect declared after "Clear search term when value changes" below
  // reads it to release `isProcessingInitialSearchTermRef` once that guarded
  // effect has had its turn in the same commit (#12187).
  const [initialSearchTermGeneration, setInitialSearchTermGeneration] =
    useState(0);

  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isProcessingInitialSearchTermRef = useRef(false);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);

  // Fetch person schema to check for required fields
  const { data: personSchema } = useEntitySchema("person");

  // Determine single entity type for allow_create_entity
  const singleEntityType = useMemo(() => {
    if (!allow_create_entity || !entityTypes || entityTypes.length !== 1) {
      return null;
    }
    return entityTypes[0];
  }, [allow_create_entity, entityTypes]);

  // Fetch schema for single entity type to check for required fields
  const { data: singleEntityTypeSchema } = useEntitySchema(
    singleEntityType || "person" // fallback to person if null (won't be used, but hook needs a value)
  );
  // Only use schema if we actually have a single entity type
  const effectiveSingleEntityTypeSchema = singleEntityType
    ? singleEntityTypeSchema
    : null;

  // Drop the previous term's rows at once (#11962): they used to stay on
  // screen — and stay selectable by Enter — for the debounce plus the whole
  // in-flight window, so a fast typist landed on a record they never asked
  // for. A pending Enter for the old term is dropped with them.
  useEffect(() => {
    setRawSearchResults([]);
    pendingEnterRef.current = false;
  }, [searchTerm]);

  // Debounce search term.
  const debouncedSearchTermValue = useDebouncedValue(searchTerm, 150);
  useEffect(() => {
    setDebouncedSearchTerm(debouncedSearchTermValue);
  }, [debouncedSearchTermValue]);

  // Determine attributes to include for search. A person's employer is no
  // longer asked for by attribute name: the result carries it resolved
  // (#11962 item 9), and asking for "Employer" too would print it twice in
  // an organization that names its attribute exactly that. Vehicles keep
  // their Status. Requesting SOME attribute for every result is what keeps
  // the card from hydrating each row itself.
  const searchAttributes = useMemo(() => {
    const attributes: string[] = [];
    if (!entityTypes || entityTypes.includes("vehicle")) {
      attributes.push(SPECIAL_VEHICLE_ATTRIBUTE);
    }
    return attributes.length > 0 ? attributes : undefined;
  }, [entityTypes]);

  // Perform search using React Query - automatically cancels previous requests
  const searchQuery = useQuery({
    queryKey: [
      "rms",
      "entities",
      "search",
      "shallow",
      debouncedSearchTerm.trim(),
      entityTypes,
      searchAttributes,
      allow_create_person,
      includeInteractions,
    ],
    queryFn: () =>
      searchEntities(
        debouncedSearchTerm,
        entityTypes,
        searchAttributes,
        undefined,
        allow_create_person,
        includeInteractions
      ),
    enabled: hasFocused && !!debouncedSearchTerm.trim(),
    staleTime: 0, // Always fetch fresh data for search
    gcTime: 0, // Don't cache search results
  });

  // Update state from query results
  useEffect(() => {
    const isQueryEnabled = hasFocused && !!debouncedSearchTerm.trim();

    if (isQueryEnabled && searchQuery.data) {
      setRawSearchResults(searchQuery.data.results);
      if (pendingEnterRef.current) {
        pendingEnterRef.current = false;
        // Honoured only while the field still has the user: Escape clears
        // the hold, and a field they have left (blur, a click elsewhere)
        // no longer has the focus that armed it (#11962 item 10).
        const first = firstSelectable(searchQuery.data.results);
        if (first && document.activeElement === inputRef.current) {
          handleSelect(first);
        }
      }
      setBackendShowCreateButton(searchQuery.data.show_create_button);
      // Format the create button name if allow_create_person is true
      // Use the original search term to preserve all-caps words, not the backend's formatted version
      const formattedName = searchQuery.data.create_button_name
        ? allow_create_person
          ? formatPersonName(debouncedSearchTerm.trim())
          : searchQuery.data.create_button_name
        : null;
      setCreateButtonName(formattedName);
      setSearchError(null);
      setDropdownOpen(true);
    } else if (!isQueryEnabled) {
      // Clear results when query is disabled (not focused or no search term)
      setRawSearchResults([]);
      setBackendShowCreateButton(false);
      setCreateButtonName(null);
    }
  }, [searchQuery.data, debouncedSearchTerm, allow_create_person, hasFocused]);

  // Update error state from query
  useEffect(() => {
    if (searchQuery.error) {
      setSearchError(
        searchQuery.error instanceof Error
          ? searchQuery.error.message
          : "Failed to search entities"
      );
      setDropdownOpen(true); // Keep dropdown open to show error
    }
  }, [searchQuery.error]);

  // Update loading state from query
  useEffect(() => {
    setIsLoading(searchQuery.isLoading);
    if (searchQuery.isLoading && hasFocused && debouncedSearchTerm.trim()) {
      setDropdownOpen(true);
    }
  }, [searchQuery.isLoading, hasFocused, debouncedSearchTerm]);

  // Map of cross-bucket entity id -> badge label (e.g. "in Mentions").
  // Built once per render so result rows can look up their badge in O(1).
  const crossBucketLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const { entity, label } of crossBucketEntities) {
      map.set(entity.id, label);
    }
    return map;
  }, [crossBucketEntities]);

  // The ids a result list never offers: the already-selected records, the
  // held value, and the record this picker sits on (no self-reference).
  // Cross-bucket entities are intentionally NOT filtered — they appear with
  // a badge.
  const unofferedIds = useMemo(() => {
    const ids = new Set(selectedEntities.map((e) => e.id));
    if (value?.id) ids.add(value.id);
    if (excludeEntityId) ids.add(excludeEntityId);
    return ids;
  }, [selectedEntities, value, excludeEntityId]);

  // Filter results based on selected entities and excluded entity ID using useMemo to avoid recursion
  const searchResults = useMemo(
    () =>
      rawSearchResults.filter((result) => !unofferedIds.has(result.entity.id)),
    [rawSearchResults, unofferedIds]
  );

  // The row Enter would take from a freshly landed result list — the same
  // first row the list renders.
  const firstSelectable = (
    results: ShallowSearchResult[]
  ): ShallowSearchResult | undefined =>
    results.find((result) => !unofferedIds.has(result.entity.id));

  // True while the typed term has no answer yet: still inside the debounce,
  // or its request is in flight. Enter waits for it (#11962).
  const typedTermInFlight =
    searchTerm.trim() !== "" &&
    (searchTerm !== debouncedSearchTerm || searchQuery.isFetching);

  // Check if person schema has required fields
  const personHasRequiredFields = useMemo(() => {
    return Boolean(
      personSchema?.facts?.some((fact: SchemaFact) => fact.is_required)
    );
  }, [personSchema]);

  // Check if single entity type schema has required fields
  const singleEntityTypeHasRequiredFields = useMemo(() => {
    if (!singleEntityType || !effectiveSingleEntityTypeSchema) return false;
    return effectiveSingleEntityTypeSchema.facts.some(
      (fact: SchemaFact) => fact.is_required
    );
  }, [singleEntityType, effectiveSingleEntityTypeSchema]);

  // Check if person is in the allowed entity types
  const canCreatePerson =
    allow_create_person && entityTypes?.includes("person");

  // Check if we can create entity (single type case)
  const canCreateSingleEntity =
    allow_create_entity && singleEntityType !== null;

  // Check if we can create entity (multiple types case)
  const canCreateMultipleEntities =
    allow_create_entity && entityTypes && entityTypes.length > 1;

  // The names the typed term must not already be: every result the term
  // answered with (the raw list, not the eight rows shown) and every record
  // this field or its sibling already holds. One rule for all three gates
  // below (#12137); the truth table lives in entityCreateGate.test.ts.
  const resultNames = useMemo(
    () => rawSearchResults.map((result) => result.entity.name),
    [rawSearchResults]
  );
  const takenNames = useMemo(
    () => [
      ...selectedEntities.map((entity) => entity.name),
      ...crossBucketEntities.map((c) => c.entity.name),
      ...(value ? [value.name] : []),
    ],
    [selectedEntities, crossBucketEntities, value]
  );

  // Person: the backend decides on plain containment; the frontend adds the
  // nickname pass it cannot make and may only hide further.
  const shouldShowCreateButton = useMemo(
    () =>
      shouldOfferCreateRow({
        term: debouncedSearchTerm,
        canCreate: Boolean(canCreatePerson),
        loading: typedTermInFlight,
        resultNames,
        takenNames,
        backendAllows: backendShowCreateButton,
        nicknameMap,
      }),
    [
      canCreatePerson,
      debouncedSearchTerm,
      backendShowCreateButton,
      resultNames,
      takenNames,
      typedTermInFlight,
      nicknameMap,
    ]
  );

  // Single kind (allow_create_entity): no backend flag, the rule alone.
  const shouldShowCreateEntityButton = useMemo(
    () =>
      shouldOfferCreateRow({
        term: debouncedSearchTerm,
        canCreate: canCreateSingleEntity,
        loading: typedTermInFlight,
        resultNames,
        takenNames,
        backendAllows: true,
        nicknameMap,
      }),
    [
      canCreateSingleEntity,
      debouncedSearchTerm,
      resultNames,
      takenNames,
      typedTermInFlight,
      nicknameMap,
    ]
  );

  // Several kinds (allow_create_entity): same rule, the modal picks the kind.
  const shouldShowCreateMultipleEntitiesButton = useMemo(
    () =>
      shouldOfferCreateRow({
        term: debouncedSearchTerm,
        canCreate: Boolean(canCreateMultipleEntities),
        loading: typedTermInFlight,
        resultNames,
        takenNames,
        backendAllows: true,
        nicknameMap,
      }),
    [
      canCreateMultipleEntities,
      debouncedSearchTerm,
      resultNames,
      takenNames,
      typedTermInFlight,
      nicknameMap,
    ]
  );

  // Determine display mode
  const isDropdownMode = variant === "dropdown";

  // Compute which special items are visible for keyboard navigation
  const showNoneOption = isDropdownMode && value;
  const showCreatePersonOption =
    canCreatePerson && createButtonName && shouldShowCreateButton;
  const showCreateEntityOption =
    canCreateSingleEntity && shouldShowCreateEntityButton && singleEntityType;
  const showCreateMultipleOption =
    canCreateMultipleEntities && shouldShowCreateMultipleEntitiesButton;

  // Determine if recents are currently visible (empty search, focused, recents available)
  const showingRecents =
    showRecents && hasRecents && !searchTerm.trim() && dropdownOpen;

  // Calculate total navigable items and index offsets
  const specialItemCount =
    (showNoneOption ? 1 : 0) +
    (showCreatePersonOption ? 1 : 0) +
    (showCreateEntityOption ? 1 : 0) +
    (showCreateMultipleOption ? 1 : 0);
  const recentsCount = showingRecents ? recents.length : 0;
  const totalNavigableItems =
    recentsCount + specialItemCount + searchResults.length;

  // Calculate index for each navigable section (-1 if not visible)
  let currentIdx = 0;
  const recentsStartIndex = showingRecents ? currentIdx : -1;
  if (showingRecents) currentIdx += recents.length;
  const noneOptionIndex = showNoneOption ? currentIdx++ : -1;
  const createPersonIndex = showCreatePersonOption ? currentIdx++ : -1;
  const createEntityIndex = showCreateEntityOption ? currentIdx++ : -1;
  const createMultipleIndex = showCreateMultipleOption ? currentIdx++ : -1;
  const searchResultsStartIndex = currentIdx;

  // Handle fast create person
  const handleFastCreatePerson = async () => {
    if (!createButtonName) return;

    // Format the name to preserve all-caps words
    const personName = allow_create_person
      ? formatPersonName(createButtonName)
      : createButtonName;

    // If person has required fields, open modal instead
    if (personHasRequiredFields) {
      setDropdownOpen(false);
      setShowCreateModal(true);
      return;
    }

    // Fast create: create person immediately with just the name
    setIsCreatingPerson(true);
    try {
      const createdPerson = await createEntity(
        queryClient,
        "person",
        personName,
        {},
        personSchema?.facts || []
      );

      // Select the newly created person
      onSelect(createdPerson);
      setSearchTerm("");
      setRawSearchResults([]);
      setBackendShowCreateButton(false);
      setCreateButtonName(null);
      setDropdownOpen(false);
    } catch (err) {
      console.error("Failed to create person:", err);
      setSearchError(
        err instanceof Error ? err.message : "Failed to create person"
      );
    } finally {
      setIsCreatingPerson(false);
    }
  };

  // Handle modal create success
  const handleModalCreateSuccess = async () => {
    setShowCreateModal(false);
    // After modal creates the person, search for it to find and select it
    const rawPersonName = createButtonName || debouncedSearchTerm.trim();
    const personName = rawPersonName
      ? allow_create_person
        ? formatPersonName(rawPersonName)
        : rawPersonName
      : null;
    if (personName) {
      try {
        const response = await searchEntities(personName, ["person"]);
        // Find the person with exact name match
        const createdPerson = response.results.find(
          (result) =>
            result.entity.name.toLowerCase() === personName.toLowerCase()
        );
        if (createdPerson) {
          onSelect(createdPerson.entity);
          setSearchTerm("");
          setRawSearchResults([]);
          setBackendShowCreateButton(false);
          setDropdownOpen(false);
        } else {
          // If not found immediately, clear search and let user search again
          setSearchTerm("");
          setRawSearchResults([]);
          setBackendShowCreateButton(false);
          setCreateButtonName(null);
          setDropdownOpen(false);
        }
      } catch (err) {
        console.error("Failed to find created person:", err);
        setSearchTerm("");
        setRawSearchResults([]);
        setBackendShowCreateButton(false);
        setCreateButtonName(null);
        setDropdownOpen(false);
      }
    }
  };

  // Handle fast create for single entity type (non-person)
  const handleFastCreateSingleEntity = async () => {
    if (!singleEntityType || !debouncedSearchTerm.trim()) return;

    const entityName = debouncedSearchTerm.trim();

    // If entity has required fields, open modal instead
    if (singleEntityTypeHasRequiredFields) {
      setDropdownOpen(false);
      setShowEntityCreateModal(true);
      return;
    }

    // Fast create: create entity immediately with just the name
    setIsCreatingEntity(true);
    try {
      const createdEntity = await createEntity(
        queryClient,
        singleEntityType,
        entityName,
        {},
        effectiveSingleEntityTypeSchema?.facts || []
      );

      // Select the newly created entity
      onSelect(createdEntity);
      setSearchTerm("");
      setRawSearchResults([]);
      setDropdownOpen(false);
    } catch (err) {
      console.error("Failed to create entity:", err);
      setSearchError(
        err instanceof Error ? err.message : "Failed to create entity"
      );
    } finally {
      setIsCreatingEntity(false);
    }
  };

  // Handle modal create success for multiple entity types
  const handleEntityModalCreateSuccess = async () => {
    setShowEntityCreateModal(false);
    // After modal creates the entity, search for it to find and select it
    const entityName = debouncedSearchTerm.trim();
    if (entityName && entityTypes && entityTypes.length > 0) {
      try {
        const response = await searchEntities(entityName, entityTypes);
        // Find the entity with exact name match
        const createdEntity = response.results.find(
          (result) =>
            result.entity.name.toLowerCase() === entityName.toLowerCase()
        );
        if (createdEntity) {
          onSelect(createdEntity.entity);
          setSearchTerm("");
          setRawSearchResults([]);
          setDropdownOpen(false);
        } else {
          // If not found immediately, clear search and let user search again
          setSearchTerm("");
          setRawSearchResults([]);
          setDropdownOpen(false);
        }
      } catch (err) {
        console.error("Failed to find created entity:", err);
        setSearchTerm("");
        setRawSearchResults([]);
        setDropdownOpen(false);
      }
    }
  };

  // Handle create button click for multiple entity types (opens modal).
  // The search term is deliberately kept -- the modal seeds the new record's
  // name from it, and `handleEntityModalCreateSuccess` searches for the
  // created record by it afterwards.
  const handleCreateMultipleEntities = () => {
    setDropdownOpen(false);
    setShowEntityCreateModal(true);
  };

  // Auto-focus input when autoFocus prop is true. The ref is already
  // attached by the time this effect runs (checked above), so there is
  // nothing to defer a tick for.
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      setHasFocused(true);
      // If we have an initial search term, make sure it's in the input
      if (initialSearchTerm && initialSearchTerm.trim()) {
        // The search term should already be set by the initialSearchTerm effect
        // Just ensure the input shows it and cursor is at end
        if (inputRef.current instanceof HTMLInputElement) {
          inputRef.current.value = initialSearchTerm;
          // Set cursor to end
          inputRef.current.setSelectionRange(
            initialSearchTerm.length,
            initialSearchTerm.length
          );
        }
      }
    }
  }, [autoFocus, initialSearchTerm]);

  // Clear search term when value changes (for dropdown mode)
  // This ensures the input shows the entity name after selection
  // But don't clear if we're processing an initialSearchTerm (user is typing to enter edit mode)
  useEffect(() => {
    // Don't clear if we're currently processing an initial search term
    if (isProcessingInitialSearchTermRef.current) {
      return;
    }
    // Also check initialSearchTerm prop as a fallback
    if (initialSearchTerm && initialSearchTerm.trim()) {
      return;
    }
    if (isDropdownMode && value) {
      // If we have a selected value, clear the search term so input shows the entity name
      setSearchTerm("");
    } else if (isDropdownMode && !value && !searchTerm.trim()) {
      // Only clear search term if it's already empty - don't clear if user is typing
      setSearchTerm("");
    }
  }, [value, isDropdownMode, initialSearchTerm, searchTerm]);

  // Releases `isProcessingInitialSearchTermRef` once the state it protects
  // has committed and the effect above has had its turn reading it in the
  // same commit — declared after that effect (not timed) so ordering, not a
  // guessed duration, is what makes this safe (#12187).
  useEffect(() => {
    if (initialSearchTermGeneration === 0) return;
    isProcessingInitialSearchTermRef.current = false;
  }, [initialSearchTermGeneration]);

  // Measure the field while the dropdown is open: the portalled dropdown
  // (tableCell or portalDropdown) is placed from it, and both dropdowns size
  // their height from the space it leaves in the viewport (#13871).
  // Measured in a layout effect so the first paint already has the height.
  const shouldPortal = tableCell || portalDropdown;
  useLayoutEffect(() => {
    if (dropdownOpen && searchRef.current) {
      const MIN_DROPDOWN_WIDTH = portalDropdown ? 0 : 400;

      const updatePosition = () => {
        if (searchRef.current) {
          const rect = searchRef.current.getBoundingClientRect();
          let horizontal: DropdownPosition["horizontal"] = null;
          if (shouldPortal) {
            const dropdownWidth = Math.max(MIN_DROPDOWN_WIDTH, rect.width);
            horizontal = {
              left: Math.min(rect.left, window.innerWidth - dropdownWidth - 8),
              width: dropdownWidth,
            };
          }
          const next: DropdownPosition = {
            top: rect.bottom,
            inputTop: rect.top,
            viewportHeight: window.innerHeight,
            horizontal,
          };
          setDropdownPosition((prev) =>
            prev && samePosition(prev, next) ? prev : next
          );
        }
      };

      // The list's own scroll moves nothing it is placed from.
      const onScroll = (event: Event) => {
        if (
          event.target instanceof Node &&
          dropdownListRef.current?.contains(event.target)
        ) {
          return;
        }
        updatePosition();
      };

      updatePosition();

      // Use ResizeObserver to watch for field size changes (e.g., column resize)
      const resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });

      if (searchRef.current) {
        resizeObserver.observe(searchRef.current);
      }

      // Listen to scroll on all scrollable containers
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [dropdownOpen, shouldPortal, portalDropdown]);

  // Moves the keyboard highlight and keeps that row inside the dropdown's
  // visible box, which holds up to fifty results (#13871). Only the keys
  // scroll: a row the pointer highlights is already in view.
  const highlightByKey = (index: number) => {
    setHighlightedIndex(index);
    dropdownListRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${index}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on the portalled dropdown
      const target = event.target as HTMLElement;
      const portalledDropdown = target.closest(".entity-search-dropdown");

      if (
        searchRef.current &&
        !searchRef.current.contains(target) &&
        !portalledDropdown
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (result: ShallowSearchResult | null) => {
    if (result) {
      onSelect(result.entity);
      // Clear search term immediately so input shows the selected entity name
      setSearchTerm("");
    } else {
      // Clearing selection
      onSelect(null);
      setSearchTerm("");
    }
    setRawSearchResults([]);
    setBackendShowCreateButton(false);
    setCreateButtonName(null);
    setIsLoading(false);
    // Close dropdown when clearing search term, keep focus for continuous searching
    setDropdownOpen(false);
    setHasFocused(true);
    // Blur or refocus the input after selection. Every result row calls
    // `preventDefault` on its own `onMouseDown`, so the input never actually
    // loses focus during the click — nothing here needs to wait a tick.
    if (inputRef.current) {
      if (blurAfterSelect && result) {
        // Blur the input after selecting an entity
        inputRef.current.blur();
      } else {
        // Refocus for continuous searching
        inputRef.current.focus();
      }
    }
  };

  const handleEntityClick = (
    event: React.MouseEvent | MouseEvent,
    result?: ShallowSearchResult
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if ((event as MouseEvent).ctrlKey || (event as MouseEvent).metaKey) {
      // Open in new tab
      const entity = result?.entity || value;
      if (entity) {
        const url = entityNavigate.prepareLink(entity);
        window.open(url, "_blank");
      }
    } else {
      // Normal selection behavior
      if (result) {
        handleSelect(result);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // If there's a selected value and user starts typing, clear it first
    if (hasSelectedValue && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      // User is typing a character - clear the selection
      onSelect(null);
      setSearchTerm("");
      // The character will be handled by onChange
      return;
    }

    // Check if dropdown is actually visible (search results or recents)
    const isDropdownVisible =
      (dropdownOpen || isLoading) &&
      (searchTerm.trim() !== "" || showingRecents);

    // Keyboard functionality support
    if (e.key === "Escape") {
      // A dismissed field owes nothing to the Enter it was holding (#11962
      // item 10): the selection must not land after the user moved on.
      pendingEnterRef.current = false;
      // Escape belongs to the nearest thing that can consume it. With the
      // list open that is this picker, and claiming the key stops it from
      // also closing whatever holds the picker. With the list shut there is
      // nothing here to dismiss, so the key passes on to the row or popover
      // around it (#11880) — otherwise Escape never left an entity-typed
      // fact row at all.
      if (!isDropdownVisible) return;
      e.preventDefault();
      setDropdownOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (
        !isDropdownVisible &&
        (searchTerm.trim() || (showRecents && hasRecents))
      ) {
        setDropdownOpen(true);
        setHighlightedIndex(0);
      } else if (isDropdownVisible && totalNavigableItems > 0) {
        highlightByKey(
          highlightedIndex < totalNavigableItems - 1 ? highlightedIndex + 1 : 0
        );
      }
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (isDropdownVisible && totalNavigableItems > 0) {
        highlightByKey(
          highlightedIndex > 0 ? highlightedIndex - 1 : totalNavigableItems - 1
        );
      }
      return;
    }

    if (e.key === "Home" && isDropdownVisible && totalNavigableItems > 0) {
      e.preventDefault();
      highlightByKey(0);
      return;
    }

    if (e.key === "End" && isDropdownVisible && totalNavigableItems > 0) {
      e.preventDefault();
      highlightByKey(totalNavigableItems - 1);
      return;
    }

    if (e.key === "Tab") {
      setDropdownOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      // Handle highlighted recent item
      if (showingRecents && recentsStartIndex >= 0) {
        const recentIdx = highlightedIndex - recentsStartIndex;
        if (recentIdx >= 0 && recentIdx < recents.length) {
          const recent = recents[recentIdx];
          onSelect({
            id: recent.id,
            name: recent.name,
            kind: recent.type as EntityKind,
          });
          setSearchTerm("");
          setDropdownOpen(false);
          setHighlightedIndex(-1);
          return;
        }
      }

      // Handle highlighted special items
      if (highlightedIndex === noneOptionIndex && showNoneOption) {
        handleSelect(null);
        return;
      }
      if (highlightedIndex === createPersonIndex && showCreatePersonOption) {
        handleFastCreatePerson();
        return;
      }
      if (highlightedIndex === createEntityIndex && showCreateEntityOption) {
        handleFastCreateSingleEntity();
        return;
      }
      if (
        highlightedIndex === createMultipleIndex &&
        showCreateMultipleOption
      ) {
        handleCreateMultipleEntities();
        return;
      }

      // Handle highlighted search result
      const searchResultIdx = highlightedIndex - searchResultsStartIndex;
      if (searchResultIdx >= 0 && searchResultIdx < searchResults.length) {
        handleSelect(searchResults[searchResultIdx]);
        return;
      }

      // The typed term has no answer yet: hold the Enter for its results
      // rather than acting on whatever an older term left behind (#11962).
      if (typedTermInFlight) {
        pendingEnterRef.current = true;
        return;
      }

      // Otherwise, use existing behavior (first available action)
      if (
        canCreatePerson &&
        shouldShowCreateButton &&
        createButtonName &&
        !isCreatingPerson
      ) {
        handleFastCreatePerson();
      } else if (
        canCreateSingleEntity &&
        shouldShowCreateEntityButton &&
        !isCreatingEntity
      ) {
        handleFastCreateSingleEntity();
      } else if (
        canCreateMultipleEntities &&
        shouldShowCreateMultipleEntitiesButton
      ) {
        handleCreateMultipleEntities();
      } else if (searchResults.length > 0) {
        handleSelect(searchResults[0]);
      }
    }
  };

  const handleFocus = () => {
    setHasFocused(true);
    // If there's a search term, show the dropdown when focusing
    if (searchTerm.trim()) {
      setDropdownOpen(true);
    } else if (showRecents) {
      // Re-read localStorage to pick up recents added by other components
      refreshFromStorage();
      setDropdownOpen(true);
    }
  };

  const handleDropdownClick = () => {
    if (variant === "dropdown" && value) {
      // When dropdown variant has a selected value, clicking opens dropdown
      setHasFocused(true);
      setDropdownOpen(true);
    } else if (showRecents && !searchTerm.trim() && !dropdownOpen) {
      // Re-open recents dropdown when clicking an already-focused input
      refreshFromStorage();
      setDropdownOpen(true);
    }
  };

  const handleClearSelection = () => {
    onSelect(null);
    setSearchTerm("");
    setRawSearchResults([]);
    setBackendShowCreateButton(false);
    setCreateButtonName(null);
    setDropdownOpen(false);
    setHasFocused(false);
  };

  const showTypeIndicators = !hideTypeIndicators;

  const buildResultSubheader = (entity: ShallowEntity): string | undefined => {
    const parts: string[] = [];
    if (showTypeIndicators) {
      parts.push(entityKindMap[entity.kind].label);
    }
    // A person's employer, resolved by the server through the org's own
    // designation (#11962): "Person · Halewood Partners" tells two people
    // of the same name apart where "Person" alone did not.
    if (entity.kind === "person" && entity.employer) {
      parts.push(entity.employer);
    }
    const crossLabel = crossBucketLabels.get(entity.id);
    if (crossLabel) {
      parts.push(crossLabel);
    }
    return parts.length > 0 ? parts.join(" · ") : undefined;
  };
  // No default "Search" placeholder — callers supply one (typically the
  // attribute's description) when they want text in the empty state.
  const placeholderText = placeholder ?? "";

  // Determine display mode (already declared above for useEffects)
  const hasSelectedValue = value && isDropdownMode;
  const displayValue = hasSelectedValue
    ? value.display_name || value.name
    : searchTerm;

  return (
    <div className="w-full">
      <div className="flex items-center gap-3">
        {/* Current entity display - only show for search variant */}
        {value && variant === "search" && (
          <div className="flex items-center gap-2">
            <EntityCard entity={value} variant="inline" />
          </div>
        )}

        {/* Search bar / Dropdown */}
        <div className="flex-1 relative" ref={searchRef}>
          <div className="relative">
            {!hasSelectedValue && !tableCell && (
              <MagnifyingGlassIcon
                className={`absolute ${leadingIconOffset} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`}
              />
            )}
            {hasSelectedValue && !tableCell && (
              <div
                className={`absolute ${leadingIconOffset} top-1/2 transform -translate-y-1/2 flex items-center gap-2`}
              >
                <EntityIcon
                  kind={value.kind}
                  className="w-4 h-4 text-gray-600"
                />
              </div>
            )}
            {hasSelectedValue && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearSelection();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 w-4 h-4 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <input
              ref={inputRef}
              type="text"
              aria-label={ariaLabel}
              placeholder={placeholderText}
              value={displayValue}
              onChange={(e) => {
                const typedValue = e.target.value;
                // If there's a selected value and user starts typing, clear it first
                if (hasSelectedValue) {
                  onSelect(null);
                  // Immediately set search term to the typed value so input updates
                  setSearchTerm(typedValue);
                } else {
                  setSearchTerm(typedValue);
                }
              }}
              onFocus={handleFocus}
              onClick={
                hasSelectedValue ? handleEntityClick : handleDropdownClick
              }
              onKeyDown={handleKeyDown}
              className={`w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)] ${
                dense
                  ? "py-1 min-h-[30px] text-[length:var(--fp-t-meta)]"
                  : "py-2 text-sm"
              } ${
                hasSelectedValue
                  ? "pl-9 pr-9 cursor-pointer bg-white text-blue-600 hover:text-blue-800 hover:underline"
                  : tableCell
                    ? "pl-2 pr-4"
                    : "pl-9 pr-4"
              } ${inputClassName}`}
            />
          </div>

          {/* Search Results Dropdown.
              Suppressed outright while a create modal is up (#11662). The
              handler that opens one closes the dropdown, but that alone isn't
              enough: the search query re-opens it whenever fresh results land,
              so a refetch behind the modal would put the list back on top of
              it -- and it does land on top, since the portalled list is
              z-[9999] against the modal overlay's z-50. */}
          {!showCreateModal &&
            !showEntityCreateModal &&
            (dropdownOpen || isLoading) &&
            (searchTerm.trim() ||
              (showRecents && hasRecents && !searchTerm.trim())) && (
              <>
                {shouldPortal ? (
                  dropdownPosition?.horizontal ? (
                    createPortal(
                      <div
                        ref={dropdownListRef}
                        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] overflow-y-auto entity-search-dropdown [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [-ms-overflow-style]:thin [scrollbar-width]:thin"
                        style={(() => {
                          const { viewportHeight, horizontal } =
                            dropdownPosition;
                          // The page cannot scroll to a fixed dropdown, so it
                          // never runs past the viewport on the side it opens
                          // toward, below the in-flow 200px floor if need be.
                          const fittedMaxHeight = (raw: number): number => {
                            const room = raw - DROPDOWN_VIEWPORT_GUTTER_PX;
                            const height = dropdownMaxHeight(
                              raw,
                              viewportHeight
                            );
                            return room > 0 ? Math.min(height, room) : height;
                          };
                          const rawBelow =
                            viewportHeight - dropdownPosition.top;
                          const rawAbove = dropdownPosition.inputTop;
                          const spaceBelow =
                            rawBelow - DROPDOWN_VIEWPORT_GUTTER_PX;
                          const spaceAbove =
                            rawAbove - DROPDOWN_VIEWPORT_GUTTER_PX;
                          const openAbove =
                            spaceBelow < 200 && spaceAbove > spaceBelow;
                          return {
                            ...(openAbove
                              ? {
                                  bottom: `${viewportHeight - dropdownPosition.inputTop}px`,
                                  maxHeight: `${fittedMaxHeight(rawAbove)}px`,
                                }
                              : {
                                  top: `${dropdownPosition.top}px`,
                                  maxHeight: `${fittedMaxHeight(rawBelow)}px`,
                                }),
                            left: `${horizontal.left}px`,
                            width: `${horizontal.width}px`,
                          };
                        })()}
                      >
                        {/* None option for dropdown variant */}
                        {isDropdownMode && value && (
                          <div
                            data-option-index={noneOptionIndex}
                            data-highlighted={
                              highlightedIndex === noneOptionIndex
                                ? "true"
                                : "false"
                            }
                            className={`flex items-center gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                              highlightedIndex === noneOptionIndex
                                ? "bg-gray-100"
                                : "hover:bg-gray-50"
                            }`}
                            onClick={() => handleSelect(null)}
                            onMouseEnter={() =>
                              setHighlightedIndex(noneOptionIndex)
                            }
                          >
                            <div className="flex flex-col gap-1 flex-1">
                              <span className="font-medium text-gray-500 text-sm italic">
                                None
                              </span>
                              <span className="text-xs text-gray-400">
                                Clear selection
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Fast Create Person Option */}
                        {canCreatePerson &&
                          createButtonName &&
                          shouldShowCreateButton && (
                            <div
                              data-option-index={createPersonIndex}
                              data-highlighted={
                                highlightedIndex === createPersonIndex
                                  ? "true"
                                  : "false"
                              }
                              className={`flex items-start gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                                highlightedIndex === createPersonIndex
                                  ? "bg-blue-100"
                                  : "hover:bg-blue-50"
                              }`}
                              onClick={handleFastCreatePerson}
                              onMouseEnter={() =>
                                setHighlightedIndex(createPersonIndex)
                              }
                            >
                              <div
                                className={`${dense ? "w-6 h-6" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0`}
                              >
                                <Plus
                                  className={`${dense ? "w-4 h-4" : "w-5 h-5"} text-blue-600`}
                                />
                              </div>
                              <div
                                className={`flex-1 min-w-0 ${dense ? "flex items-baseline gap-2" : ""}`}
                              >
                                <span className="font-medium text-blue-600 block truncate">
                                  {createButtonName.length > 70
                                    ? createButtonName.substring(0, 70) + "..."
                                    : createButtonName}
                                </span>
                                <div className="text-xs text-gray-500">
                                  Person (New)
                                </div>
                              </div>
                              {isCreatingPerson && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              )}
                            </div>
                          )}

                        {/* Fast Create Single Entity Type Option (allow_create_entity) */}
                        {canCreateSingleEntity &&
                          shouldShowCreateEntityButton &&
                          singleEntityType && (
                            <div
                              data-option-index={createEntityIndex}
                              data-highlighted={
                                highlightedIndex === createEntityIndex
                                  ? "true"
                                  : "false"
                              }
                              className={`flex items-start gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                                highlightedIndex === createEntityIndex
                                  ? "bg-blue-100"
                                  : "hover:bg-blue-50"
                              }`}
                              onClick={handleFastCreateSingleEntity}
                              onMouseEnter={() =>
                                setHighlightedIndex(createEntityIndex)
                              }
                            >
                              <div
                                className={`${dense ? "w-6 h-6" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0`}
                              >
                                <Plus
                                  className={`${dense ? "w-4 h-4" : "w-5 h-5"} text-blue-600`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-blue-600 block truncate">
                                  {debouncedSearchTerm.trim().length > 70
                                    ? debouncedSearchTerm
                                        .trim()
                                        .substring(0, 70) + "..."
                                    : debouncedSearchTerm.trim()}
                                </span>
                                <div className="text-xs text-gray-500">
                                  {entityKindMap[singleEntityType]?.label ||
                                    singleEntityType}{" "}
                                  (New)
                                </div>
                              </div>
                              {isCreatingEntity && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              )}
                            </div>
                          )}

                        {/* Create Multiple Entity Types Option (allow_create_entity) */}
                        {canCreateMultipleEntities &&
                          shouldShowCreateMultipleEntitiesButton && (
                            <div
                              data-option-index={createMultipleIndex}
                              data-highlighted={
                                highlightedIndex === createMultipleIndex
                                  ? "true"
                                  : "false"
                              }
                              className={`flex items-start gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                                highlightedIndex === createMultipleIndex
                                  ? "bg-blue-100"
                                  : "hover:bg-blue-50"
                              }`}
                              onClick={handleCreateMultipleEntities}
                              onMouseEnter={() =>
                                setHighlightedIndex(createMultipleIndex)
                              }
                            >
                              <div
                                className={`${dense ? "w-6 h-6" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0`}
                              >
                                <Plus
                                  className={`${dense ? "w-4 h-4" : "w-5 h-5"} text-blue-600`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-blue-600 block truncate">
                                  {debouncedSearchTerm.trim().length > 70
                                    ? debouncedSearchTerm
                                        .trim()
                                        .substring(0, 70) + "..."
                                    : debouncedSearchTerm.trim()}
                                </span>
                                <div className="text-xs text-gray-500">
                                  Create New
                                </div>
                              </div>
                            </div>
                          )}

                        {(isLoading || typedTermInFlight) &&
                        searchResults.length === 0 ? (
                          <div className="px-3 py-4">
                            {[...Array(3)].map((_, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-3 px-3 py-2 animate-pulse"
                              >
                                <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0"></div>
                                <div className="flex flex-col gap-1 flex-1">
                                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : searchError ? (
                          <div className="px-3 py-4 text-center text-red-500 text-sm">
                            {searchError}
                          </div>
                        ) : searchResults.length === 0 &&
                          (!isDropdownMode || !value) ? (
                          <div className="px-3 py-4 text-center text-gray-500 text-sm">
                            No results found.
                          </div>
                        ) : (
                          searchResults.map((result, index) => (
                            <div
                              key={result.entity.id}
                              data-option-index={
                                searchResultsStartIndex + index
                              }
                              data-highlighted={
                                highlightedIndex ===
                                searchResultsStartIndex + index
                                  ? "true"
                                  : "false"
                              }
                              className={`flex items-center gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors ${
                                highlightedIndex ===
                                searchResultsStartIndex + index
                                  ? "bg-gray-100"
                                  : "hover:bg-gray-50"
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Use onMouseDown instead of onClick to fire before click-outside handler
                                handleEntityClick(e.nativeEvent, result);
                              }}
                              onMouseEnter={() =>
                                setHighlightedIndex(
                                  searchResultsStartIndex + index
                                )
                              }
                            >
                              <EntityCard
                                disableLink
                                hideIcon={!showTypeIndicators}
                                entity={result.entity}
                                // Same list anatomy as the non-portal
                                // branch (gaps-v8 item 6, #11426).
                                variant="list"
                                prefetchedFacts={result.facts}
                                subheader={buildResultSubheader(result.entity)}
                              />
                            </div>
                          ))
                        )}
                      </div>,
                      document.body
                    )
                  ) : null
                ) : (
                  <div
                    ref={dropdownListRef}
                    className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-y-auto"
                    style={{
                      marginTop: IN_FLOW_DROPDOWN_OFFSET_PX,
                      // Until the field is measured, the fixed cap the
                      // dropdown had before it followed the viewport.
                      maxHeight: dropdownPosition
                        ? dropdownMaxHeight(
                            dropdownPosition.viewportHeight -
                              dropdownPosition.top -
                              IN_FLOW_DROPDOWN_OFFSET_PX,
                            dropdownPosition.viewportHeight
                          )
                        : UNMEASURED_DROPDOWN_MAX_HEIGHT_PX,
                    }}
                  >
                    {/* Recently viewed items — shown when focused with empty search */}
                    {showRecents &&
                      hasRecents &&
                      !searchTerm.trim() &&
                      recents.map((recent, index) => (
                        <div
                          key={recent.id}
                          {...maybePrefetchAttrs({
                            kind: recent.type,
                            id: recent.id,
                          })}
                          data-option-index={recentsStartIndex + index}
                          data-highlighted={
                            highlightedIndex === recentsStartIndex + index
                              ? "true"
                              : "false"
                          }
                          className={`flex items-center gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors ${
                            highlightedIndex === recentsStartIndex + index
                              ? "bg-gray-100"
                              : "hover:bg-gray-50"
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelect({
                              id: recent.id,
                              name: recent.name,
                              kind: recent.type as EntityKind,
                            });
                            setSearchTerm("");
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={() =>
                            setHighlightedIndex(recentsStartIndex + index)
                          }
                        >
                          {showTypeIndicators && (
                            <EntityIcon
                              kind={recent.type as EntityKind}
                              size={16}
                              className="text-gray-400 flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-800 truncate block">
                              {recent.name}
                            </span>
                            {showTypeIndicators && (
                              <span className="text-xs text-gray-400">
                                {entityKindMap[recent.type as EntityKind]
                                  ?.label ?? recent.type}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}

                    {/* None option for dropdown variant */}
                    {isDropdownMode && value && (
                      <div
                        data-option-index={noneOptionIndex}
                        data-highlighted={
                          highlightedIndex === noneOptionIndex
                            ? "true"
                            : "false"
                        }
                        className={`flex items-center gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                          highlightedIndex === noneOptionIndex
                            ? "bg-gray-100"
                            : "hover:bg-gray-50"
                        }`}
                        onClick={() => handleSelect(null)}
                        onMouseEnter={() =>
                          setHighlightedIndex(noneOptionIndex)
                        }
                      >
                        <div className="flex flex-col gap-1 flex-1">
                          <span className="font-medium text-gray-500 text-sm italic">
                            None
                          </span>
                          <span className="text-xs text-gray-400">
                            Clear selection
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Fast Create Person Option */}
                    {canCreatePerson &&
                      createButtonName &&
                      shouldShowCreateButton && (
                        <div
                          data-option-index={createPersonIndex}
                          data-highlighted={
                            highlightedIndex === createPersonIndex
                              ? "true"
                              : "false"
                          }
                          className={`flex items-start gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                            highlightedIndex === createPersonIndex
                              ? "bg-blue-100"
                              : "hover:bg-blue-50"
                          }`}
                          onClick={handleFastCreatePerson}
                          onMouseEnter={() =>
                            setHighlightedIndex(createPersonIndex)
                          }
                        >
                          <div
                            className={`${dense ? "w-6 h-6" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0`}
                          >
                            <Plus
                              className={`${dense ? "w-4 h-4" : "w-5 h-5"} text-blue-600`}
                            />
                          </div>
                          <div
                            className={`flex-1 min-w-0 ${dense ? "flex items-baseline gap-2" : ""}`}
                          >
                            <span className="font-medium text-blue-600 block truncate">
                              {createButtonName.length > 70
                                ? createButtonName.substring(0, 70) + "..."
                                : createButtonName}
                            </span>
                            <div className="text-xs text-gray-500">
                              Person (New)
                            </div>
                          </div>
                          {isCreatingPerson && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          )}
                        </div>
                      )}

                    {/* Fast Create Single Entity Type Option (allow_create_entity) */}
                    {canCreateSingleEntity &&
                      shouldShowCreateEntityButton &&
                      singleEntityType && (
                        <div
                          data-option-index={createEntityIndex}
                          data-highlighted={
                            highlightedIndex === createEntityIndex
                              ? "true"
                              : "false"
                          }
                          className={`flex items-start gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                            highlightedIndex === createEntityIndex
                              ? "bg-blue-100"
                              : "hover:bg-blue-50"
                          }`}
                          onClick={handleFastCreateSingleEntity}
                          onMouseEnter={() =>
                            setHighlightedIndex(createEntityIndex)
                          }
                        >
                          <div
                            className={`${dense ? "w-6 h-6" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0`}
                          >
                            <Plus
                              className={`${dense ? "w-4 h-4" : "w-5 h-5"} text-blue-600`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-blue-600 block truncate">
                              {debouncedSearchTerm.trim().length > 70
                                ? debouncedSearchTerm.trim().substring(0, 70) +
                                  "..."
                                : debouncedSearchTerm.trim()}
                            </span>
                            <div className="text-xs text-gray-500">
                              {entityKindMap[singleEntityType]?.label ||
                                singleEntityType}{" "}
                              (New)
                            </div>
                          </div>
                          {isCreatingEntity && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          )}
                        </div>
                      )}

                    {/* Create Multiple Entity Types Option (allow_create_entity) */}
                    {canCreateMultipleEntities &&
                      shouldShowCreateMultipleEntitiesButton && (
                        <div
                          data-option-index={createMultipleIndex}
                          data-highlighted={
                            highlightedIndex === createMultipleIndex
                              ? "true"
                              : "false"
                          }
                          className={`flex items-start gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors border-b border-gray-100 ${
                            highlightedIndex === createMultipleIndex
                              ? "bg-blue-100"
                              : "hover:bg-blue-50"
                          }`}
                          onClick={handleCreateMultipleEntities}
                          onMouseEnter={() =>
                            setHighlightedIndex(createMultipleIndex)
                          }
                        >
                          <div
                            className={`${dense ? "w-6 h-6" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0`}
                          >
                            <Plus
                              className={`${dense ? "w-4 h-4" : "w-5 h-5"} text-blue-600`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-blue-600 block truncate">
                              {debouncedSearchTerm.trim().length > 70
                                ? debouncedSearchTerm.trim().substring(0, 70) +
                                  "..."
                                : debouncedSearchTerm.trim()}
                            </span>
                            <div className="text-xs text-gray-500">
                              Create New
                            </div>
                          </div>
                        </div>
                      )}

                    {(isLoading || typedTermInFlight) &&
                    searchResults.length === 0 ? (
                      <div className="px-3 py-4">
                        {[...Array(3)].map((_, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 px-3 py-2 animate-pulse"
                          >
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0"></div>
                            <div className="flex flex-col gap-1 flex-1">
                              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : searchError ? (
                      <div className="px-3 py-4 text-center text-red-500 text-sm">
                        {searchError}
                      </div>
                    ) : searchResults.length === 0 &&
                      (!isDropdownMode || !value) &&
                      searchTerm.trim() ? (
                      <div className="px-3 py-4 text-center text-gray-500 text-sm">
                        No results found.
                      </div>
                    ) : (
                      searchResults.map((result, index) => (
                        <div
                          key={result.entity.id}
                          {...maybePrefetchAttrs(result.entity)}
                          data-option-index={searchResultsStartIndex + index}
                          data-highlighted={
                            highlightedIndex === searchResultsStartIndex + index
                              ? "true"
                              : "false"
                          }
                          className={`flex items-center gap-3 px-3 py-1 min-h-10 cursor-pointer transition-colors ${
                            highlightedIndex === searchResultsStartIndex + index
                              ? "bg-gray-100"
                              : "hover:bg-gray-50"
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Use onMouseDown instead of onClick to fire before click-outside handler
                            handleEntityClick(e.nativeEvent, result);
                          }}
                          onMouseEnter={() =>
                            setHighlightedIndex(searchResultsStartIndex + index)
                          }
                        >
                          <EntityCard
                            disableLink
                            hideIcon={!showTypeIndicators}
                            entity={result.entity}
                            // The recents-row anatomy (gaps-v8 item 6,
                            // #11426): one list, one design, whether the
                            // rows came from a term or from history.
                            variant="list"
                            prefetchedFacts={result.facts}
                            subheader={buildResultSubheader(result.entity)}
                          />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
        </div>
      </div>

      {/* Entity Creation Modal for Person */}
      {showCreateModal && (
        <EntityCreationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleModalCreateSuccess}
          preselectedType="person"
          initialName={
            createButtonName || debouncedSearchTerm.trim()
              ? allow_create_person
                ? formatPersonName(
                    createButtonName || debouncedSearchTerm.trim()
                  )
                : createButtonName || debouncedSearchTerm.trim()
              : undefined
          }
          preventNavigation={true}
        />
      )}

      {/* Entity Creation Modal for Multiple Entity Types (allow_create_entity) */}
      {showEntityCreateModal && entityTypes && entityTypes.length > 1 && (
        <EntityCreationModal
          isOpen={showEntityCreateModal}
          onClose={() => setShowEntityCreateModal(false)}
          onSuccess={handleEntityModalCreateSuccess}
          initialName={debouncedSearchTerm.trim() || undefined}
          preventNavigation={true}
          // Set when the attribute already determines the kind, which takes
          // the modal straight to that kind's form instead of asking.
          preselectedType={createEntityKind ?? undefined}
          excludeTypes={
            entityTypes
              ? (Object.keys(entityKindMap).filter(
                  (kind) => !entityTypes.includes(kind as EntityKind)
                ) as EntityKind[])
              : undefined
          }
        />
      )}

      {/* Entity Creation Modal for Single Entity Type with Required Fields (allow_create_entity) */}
      {showEntityCreateModal &&
        singleEntityType &&
        entityTypes &&
        entityTypes.length === 1 && (
          <EntityCreationModal
            isOpen={showEntityCreateModal}
            onClose={() => setShowEntityCreateModal(false)}
            onSuccess={handleEntityModalCreateSuccess}
            preselectedType={singleEntityType}
            initialName={debouncedSearchTerm.trim() || undefined}
            preventNavigation={true}
          />
        )}
    </div>
  );
};

export default EntitySearchInput;
